import { useEffect, useState } from "react";
import { Phone, PhoneCall, ExternalLink, FileText } from "lucide-react";
import { api } from "@/lib/api";

const ACTIONS = [
  {
    key: "call_intent",
    label: "Abren el botón de llamada (móvil)",
    hint: "Pulsan «Llámanos 914 020 331» y ven los horarios",
    Icon: Phone,
  },
  {
    key: "call_now",
    label: "Llamada iniciada",
    hint: "Pulsan «Llamar al 914 020 331» y se abre el marcador",
    Icon: PhoneCall,
  },
  {
    key: "booking_online",
    label: "Reservar online",
    hint: "Clics en el botón de reserva de cada viaje",
    Icon: ExternalLink,
  },
  {
    key: "dossier_pdf",
    label: "Descargas de dossier PDF",
    hint: "Clics en «Descargar dossier completo»",
    Icon: FileText,
  },
];

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function ClickStatsPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/clicks/stats")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) return null;

  return (
    <section className="mb-10" data-testid="click-stats-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-brand-primary">
            Clics en botones clave
          </h2>
          <p className="text-sm text-brand-muted mt-1">
            Midiendo desde el {fmtDate(data.tracking_since) || "1 de octubre de 2026"}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {ACTIONS.map(({ key, label, hint, Icon }) => {
          const c = data.by_action?.[key] || {};
          return (
            <div key={key} className="card-soft p-5" data-testid={`click-card-${key}`}>
              <div className="flex items-center gap-2 text-brand-primary">
                <Icon className="h-4 w-4" />
                <p className="text-sm font-medium leading-tight">{label}</p>
              </div>
              <p className="font-outfit text-4xl font-semibold text-brand-primary mt-3 tabular-nums">
                {c.total ?? 0}
              </p>
              <p className="text-[11px] uppercase tracking-wider text-brand-muted mt-1">Total</p>
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-brand-line text-center">
                <div>
                  <p className="font-outfit text-lg font-semibold tabular-nums">{c.today ?? 0}</p>
                  <p className="text-[10px] uppercase tracking-wider text-brand-muted">Hoy</p>
                </div>
                <div>
                  <p className="font-outfit text-lg font-semibold tabular-nums">{c.last_7d ?? 0}</p>
                  <p className="text-[10px] uppercase tracking-wider text-brand-muted">7 días</p>
                </div>
                <div>
                  <p className="font-outfit text-lg font-semibold tabular-nums">{c.last_30d ?? 0}</p>
                  <p className="text-[10px] uppercase tracking-wider text-brand-muted">30 días</p>
                </div>
              </div>
              <p className="text-xs text-brand-muted mt-4 leading-relaxed">{hint}</p>
            </div>
          );
        })}
      </div>

      {data.by_trip?.length > 0 && (
        <div className="card-soft mt-4 overflow-hidden" data-testid="click-stats-by-trip">
          <table className="w-full text-sm">
            <thead className="bg-brand-surface/70 text-brand-muted">
              <tr>
                <th className="text-left font-medium px-5 py-3">Viaje</th>
                <th className="text-right font-medium px-5 py-3">Reservar online</th>
                <th className="text-right font-medium px-5 py-3">Dossier PDF</th>
              </tr>
            </thead>
            <tbody>
              {data.by_trip.map((t) => (
                <tr key={t.slug} className="border-t border-brand-line">
                  <td className="px-5 py-3">{t.name || t.slug}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{t.booking_online}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{t.dossier_pdf}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
